import React from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { FUEL_TYPES, COMPANY_NAME } from './constants';

export default function VehicleFormDialog({
  open,
  onClose,
  vehicleForm,
  setVehicleForm,
  editIdx,
  employees,
  onSave,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const selectedEmployee = employees.find(
    (e) => e._id === (vehicleForm.owner_employee?._id || vehicleForm.owner_employee),
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {editIdx !== null ? 'แก้ไขข้อมูลรถยนต์' : 'เพิ่มรถยนต์'}
        {isMobile && <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>}
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} className="mt-0">
          <Grid item xs={12} sm={6}>
            <TextField
              label="ทะเบียนรถ *"
              fullWidth size="small"
              value={vehicleForm.license_plate}
              onChange={(e) => setVehicleForm({ ...vehicleForm, license_plate: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="ยี่ห้อ"
              fullWidth size="small"
              value={vehicleForm.brand}
              onChange={(e) => setVehicleForm({ ...vehicleForm, brand: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="รุ่น"
              fullWidth size="small"
              value={vehicleForm.model_name}
              onChange={(e) => setVehicleForm({ ...vehicleForm, model_name: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              select label="ประเภทเชื้อเพลิง"
              fullWidth size="small"
              value={vehicleForm.fuel_type}
              onChange={(e) => setVehicleForm({ ...vehicleForm, fuel_type: e.target.value })}
            >
              {FUEL_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="อัตราเบิกน้ำมัน"
              fullWidth size="small" type="number"
              value={vehicleForm.rate_per_km}
              onChange={(e) => setVehicleForm({ ...vehicleForm, rate_per_km: Number(e.target.value) })}
              InputProps={{ endAdornment: <InputAdornment position="end">บาท/กม.</InputAdornment> }}
              helperText="อัตรามาตรฐาน = 4 บาท/กม."
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="เลขไมล์ตั้งต้น"
              fullWidth size="small" type="number"
              value={vehicleForm.initial_mileage}
              onChange={(e) => setVehicleForm({ ...vehicleForm, initial_mileage: Number(e.target.value) })}
              InputProps={{ endAdornment: <InputAdornment position="end">กม.</InputAdornment> }}
              helperText="เลขไมล์ ณ วันที่เพิ่มเข้าระบบ"
            />
          </Grid>

          <Grid item xs={12}>
            <Divider><Typography variant="caption" color="text.secondary">เจ้าของรถ</Typography></Divider>
          </Grid>
          <Grid item xs={12}>
            <Box className="flex gap-2 flex-wrap">
              {[
                { value: 'company', label: 'รถบริษัท' },
                { value: 'personal', label: 'รถส่วนตัว' },
                { value: 'other', label: 'อื่นๆ' },
              ].map(({ value, label }) => (
                <FormControlLabel
                  key={value}
                  label={label}
                  control={
                    <Checkbox
                      size="small"
                      checked={vehicleForm.owner_type === value}
                      onChange={() => setVehicleForm({
                        ...vehicleForm,
                        owner_type: value,
                        owner_employee: null,
                        owner_name: '',
                        owner_reason: '',
                      })}
                    />
                  }
                />
              ))}
            </Box>
          </Grid>

          {vehicleForm.owner_type === 'company' && (
            <Grid item xs={12}>
              <TextField label="เจ้าของรถ" fullWidth size="small" value={COMPANY_NAME} disabled />
            </Grid>
          )}

          {vehicleForm.owner_type === 'personal' && (
            <Grid item xs={12}>
              <Autocomplete
                options={employees}
                getOptionLabel={(emp) => `${emp.firstname || ''} ${emp.lastname || ''}${emp.nickname_name ? ` (${emp.nickname_name})` : ''}`.trim()}
                isOptionEqualToValue={(opt, val) => opt._id === val._id}
                value={selectedEmployee || null}
                onChange={(_, newVal) => setVehicleForm({ ...vehicleForm, owner_employee: newVal?._id || '' })}
                renderInput={(params) => <TextField {...params} label="เลือกเจ้าของรถ (พนักงาน)" size="small" fullWidth />}
                noOptionsText="ไม่พบพนักงาน"
              />
            </Grid>
          )}

          {vehicleForm.owner_type === 'other' && (
            <>
              <Grid item xs={12}>
                <TextField
                  label="ชื่อเจ้าของรถ"
                  fullWidth size="small"
                  value={vehicleForm.owner_name}
                  onChange={(e) => setVehicleForm({ ...vehicleForm, owner_name: e.target.value })}
                  placeholder="ชื่อ-นามสกุล เจ้าของรถ"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="เหตุผล"
                  fullWidth size="small" multiline rows={2}
                  value={vehicleForm.owner_reason}
                  onChange={(e) => setVehicleForm({ ...vehicleForm, owner_reason: e.target.value })}
                  placeholder="เหตุผลที่นำรถบุคคลภายนอกเข้าระบบ"
                />
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>ยกเลิก</Button>
        <Button
          variant="contained"
          onClick={onSave}
          disabled={
            !vehicleForm.license_plate ||
            (vehicleForm.owner_type === 'personal' && !vehicleForm.owner_employee) ||
            (vehicleForm.owner_type === 'other' && !vehicleForm.owner_name)
          }
        >
          บันทึก
        </Button>
      </DialogActions>
    </Dialog>
  );
}
